/**
 * Recurring-edit payload helpers for the recurring-SERIES edit flow
 * (Flutter `appointments_tab.dart:147-265` this/following/all menu).
 *
 * NOTE: the drag-drop / reschedule-button scope PICKER dialog that used to
 * live here was removed for Flutter parity — reschedule never prompts for a
 * scope (see PR "reschedule opens the inline edit panel"). The scope choice
 * now originates only in the series edit menu; this module keeps the
 * payload-shaping helper + type it needs.
 *
 * Wire-shape contract (dev-curl verified 2026-05-13):
 *
 *   this-only   → editRecurring: null
 *   following   → editRecurring: {
 *                   appointmentDate: <source.start ISO>,
 *                   allAppointment: false,
 *                   dataUpdate: <changed-fields[]>
 *                 }
 *   all         → editRecurring: {
 *                   appointmentDate: null,
 *                   allAppointment: true,
 *                   dataUpdate: <changed-fields[]>
 *                 }
 */

import type { RecurringScope } from './cancel-appointment-dialog';

export type { RecurringScope };

// ─── Helpers ───────────────────────────────────────────────────────────

export interface EditRecurringPayload {
  appointmentDate: string | null;
  allAppointment: boolean;
  /**
   * Field names that changed in this edit — backend uses this to decide
   * which columns to propagate across the series. Flutter sends e.g.
   * `["start", "end"]` when only time was rescheduled. The consumer
   * computes this by diffing the loaded appointment vs the new body.
   */
  dataUpdate: string[];
}

/**
 * Translates a scope choice + source occurrence + changed field names
 * into the `editRecurring` body field for PUT /api/Appointment/{id}.
 *
 * Returns `null` for the "this-only" scope — that's the wire shape
 * Flutter sends when no series-wide propagation is needed.
 */
export function buildEditRecurringPayload(
  scope: RecurringScope,
  occurrenceStartISO: string | undefined,
  dataUpdate: string[],
): EditRecurringPayload | null {
  if (scope === 'this-only') return null;
  if (scope === 'all') {
    return { appointmentDate: null, allAppointment: true, dataUpdate };
  }
  // 'following' — backend reads `appointmentDate` as the cutoff.
  return {
    appointmentDate: occurrenceStartISO ?? null,
    allAppointment: false,
    dataUpdate,
  };
}
